import { useCallback, useEffect, useRef, useState } from "react";
import { Box, HStack, IconButton, Text } from "@chakra-ui/react";
import {
  BoldIcon,
  ItalicIcon,
  Link2Icon,
  ListIcon,
  ListOrderedIcon,
  UnderlineIcon,
} from "lucide-react";
import { htmlToMarkdown, renderBioHtml } from "../utils/bioMarkdown";
import { BIO_CONTENT_CSS } from "./bioContentCss";

// Matches customer-app's editor. Advisory only in support-app: support staff may
// deliberately save a longer bio than a creator could author themselves, so this
// never blocks the page Save.
const MAX_CHARS = 1050;

type ActiveStates = {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  bullet: boolean;
  numbered: boolean;
};

const NO_ACTIVE_STATES: ActiveStates = {
  bold: false,
  italic: false,
  underline: false,
  bullet: false,
  numbered: false,
};

function characterCountLabel(currentLength: number, maxLength: number): string {
  const remaining = maxLength - currentLength;
  return remaining >= 0
    ? `${remaining} characters left`
    : `${Math.abs(remaining)} characters over limit`;
}

/**
 * WYSIWYG bio editor, ported from the one a creator uses on their own profile in
 * customer-app (`src/pages/creator_profile/Profile.tsx`). `value` and `onChange`
 * both speak markdown, which is what the `bio` column stores.
 */
export default function BioEditor({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (markdown: string) => void;
}) {
  const editableRef = useRef<HTMLDivElement | null>(null);
  const selectionRef = useRef<Range | null>(null);
  const [activeStates, setActiveStates] = useState<ActiveStates>(NO_ACTIVE_STATES);
  const [length, setLength] = useState((value ?? "").length);
  const isOverLimit = length > MAX_CHARS;

  // contentEditable is uncontrolled: seed innerHTML ONCE, on mount. Re-running
  // this on `value` changes would reset the caret to the start of the editor on
  // every keystroke. CreatorDetail remounts us with a `key` when the profile
  // changes, which is what makes the mount-only seed correct.
  useEffect(() => {
    if (editableRef.current) {
      editableRef.current.innerHTML = renderBioHtml(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = useCallback(() => {
    const markdown = htmlToMarkdown(editableRef.current?.innerHTML || "");
    setLength(markdown.length);
    onChange(markdown);
  }, [onChange]);

  const refreshActiveStates = useCallback(() => {
    const selection = window.getSelection();
    const editor = editableRef.current;
    if (!editor) return;
    // Only read command state when the caret is genuinely inside THIS editor.
    // queryCommandState otherwise reports whatever contentEditable was focused
    // last, which would let the toolbar lie about our selection.
    const inEditor =
      selection &&
      selection.rangeCount > 0 &&
      editor.contains(selection.getRangeAt(0).commonAncestorContainer);
    if (!inEditor || !selection) return;
    selectionRef.current = selection.getRangeAt(0).cloneRange();
    setActiveStates({
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      bullet: document.queryCommandState("insertUnorderedList"),
      numbered: document.queryCommandState("insertOrderedList"),
    });
  }, []);

  useEffect(() => {
    document.addEventListener("selectionchange", refreshActiveStates);
    return () =>
      document.removeEventListener("selectionchange", refreshActiveStates);
  }, [refreshActiveStates]);

  const restoreSelection = (): boolean => {
    const editor = editableRef.current;
    const range = selectionRef.current;
    const selection = window.getSelection();
    if (
      !editor ||
      !range ||
      !selection ||
      !editor.contains(range.commonAncestorContainer)
    ) {
      return false;
    }
    selection.removeAllRanges();
    selection.addRange(range);
    editor.focus();
    return true;
  };

  const applyFormat = (command: string) => {
    if (!restoreSelection()) editableRef.current?.focus();
    document.execCommand(command);
    emit();
    refreshActiveStates();
  };

  const openLinkPrompt = () => {
    restoreSelection();
    const selection = window.getSelection();
    if (
      !selection ||
      selection.rangeCount === 0 ||
      selection.toString().length === 0
    ) {
      window.alert("Please select text first to create a link");
      editableRef.current?.focus();
      return;
    }
    const range = selection.getRangeAt(0);
    if (!editableRef.current?.contains(range.commonAncestorContainer)) {
      window.alert("Please select text within the editor");
      editableRef.current?.focus();
      return;
    }
    const url = window.prompt("Enter URL:");
    if (!url) {
      editableRef.current?.focus();
      return;
    }
    const href =
      url.startsWith("http://") || url.startsWith("https://")
        ? url
        : `https://${url}`;

    const link = document.createElement("a");
    link.href = href;
    link.textContent = range.toString();
    link.target = "_blank";
    link.rel = "noopener noreferrer";

    range.deleteContents();
    range.insertNode(link);
    range.setStartAfter(link);
    range.setEndAfter(link);
    selection.removeAllRanges();
    selection.addRange(range);
    selectionRef.current = range.cloneRange();

    emit();
    editableRef.current?.focus();
    refreshActiveStates();
  };

  // Written out rather than mapped over a `tools` array: collecting the handlers
  // into an array during render trips `react-hooks/refs`, because the compiler
  // cannot prove closures reading `editableRef.current` only run in event
  // handlers. Inline JSX makes that obvious to it.
  const toolButtonProps = {
    size: "sm",
    variant: "ghost",
    // Without preventDefault the mousedown blurs the editor, collapsing the
    // selection before the command runs, and the button no-ops.
    onMouseDown: (event: React.MouseEvent) => event.preventDefault(),
  } as const;

  const activeBg = (isActive: boolean) => (isActive ? "blue.100" : undefined);

  return (
    <Box>
      <HStack spacing={1} mb={1.5} flexWrap="wrap">
        <IconButton
          {...toolButtonProps}
          icon={<BoldIcon size={16} />}
          aria-label="Bold"
          aria-pressed={activeStates.bold}
          bg={activeBg(activeStates.bold)}
          onClick={() => applyFormat("bold")}
        />
        <IconButton
          {...toolButtonProps}
          icon={<ItalicIcon size={16} />}
          aria-label="Italic"
          aria-pressed={activeStates.italic}
          bg={activeBg(activeStates.italic)}
          onClick={() => applyFormat("italic")}
        />
        <IconButton
          {...toolButtonProps}
          icon={<UnderlineIcon size={16} />}
          aria-label="Underline"
          aria-pressed={activeStates.underline}
          bg={activeBg(activeStates.underline)}
          onClick={() => applyFormat("underline")}
        />
        <IconButton
          {...toolButtonProps}
          icon={<Link2Icon size={16} />}
          aria-label="Link"
          onClick={() => openLinkPrompt()}
        />
        <IconButton
          {...toolButtonProps}
          icon={<ListIcon size={16} />}
          aria-label="Bullet list"
          aria-pressed={activeStates.bullet}
          bg={activeBg(activeStates.bullet)}
          onClick={() => applyFormat("insertUnorderedList")}
        />
        <IconButton
          {...toolButtonProps}
          icon={<ListOrderedIcon size={16} />}
          aria-label="Numbered list"
          aria-pressed={activeStates.numbered}
          bg={activeBg(activeStates.numbered)}
          onClick={() => applyFormat("insertOrderedList")}
        />
      </HStack>

      <Box
        ref={editableRef}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        role="textbox"
        aria-label="Bio"
        aria-multiline="true"
        aria-describedby="bio-character-count"
        minH="8rem"
        w="100%"
        p={2}
        bg="white"
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="md"
        outline="none"
        whiteSpace="normal"
        wordBreak="break-word"
        _focusVisible={{
          borderColor: "blue.500",
          boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
        }}
        css={{
          "&:empty:before": {
            content: '"A short, descriptive bio about this creator"',
            color: "var(--chakra-colors-gray-400)",
          },
          ...BIO_CONTENT_CSS,
        }}
      />

      <Text
        id="bio-character-count"
        mt={1}
        fontSize="xs"
        color={isOverLimit ? "red.600" : "gray.500"}
        role="status"
        aria-live="polite"
      >
        {characterCountLabel(length, MAX_CHARS)}
      </Text>
    </Box>
  );
}
